import { useState, useEffect, useRef } from 'react';
import '../App.css';

// "Deletion Test Collector" → "DC". The name may be missing (users.name is
// nullable, and a brand may have no title), so fall back to a glyph rather than
// an empty circle, which reads as a failed image.
export const initialsOf = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return null;
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// The account control in the top bar of both dashboards: a soft monogram that
// opens a small menu.
//
// The user menu is an identity header (monogram + name), a solid "My Account"
// pill into the account page (where Estate planning also lives), and a quiet
// Log out. Account deletion now lives inside the account page (its own section)
// and on the public /delete-account route, so it is no longer a peer here.
//
// `onSettings` is optional: the brand dashboard has no account page, so it
// renders the plain Log out button alone (its menu is unchanged).
const AccountMenu = ({ name, onLogout, onSettings }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const initials = initialsOf(name);

    // Close on outside click or Escape. Both listeners are attached only while
    // the menu is open, so the closed state costs nothing.
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className="collection-account" ref={ref}>
            <button
                className="collection-avatar"
                onClick={() => setOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Account"
            >
                {initials || (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111"
                         strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                    </svg>
                )}
            </button>
            {open && (
                <div className="account-menu" role="menu">
                    {onSettings ? (
                        <>
                            <button className="account-menu-row" role="menuitem" onClick={onSettings}>
                                My Account
                            </button>
                            <button className="account-menu-row" role="menuitem" onClick={onLogout}>
                                Log out
                            </button>
                        </>
                    ) : (
                        <button
                            className="dpp-button secondary account-menu-logout"
                            role="menuitem"
                            onClick={onLogout}
                        >
                            Log out
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default AccountMenu;
